import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import moment from 'moment';
import actions from './actions';
import Api from '../../../../helpers/api';
import { sendRequest } from '../../../../helpers/saga';
import { logger } from '../../../../helpers/logger';
import notifications from '../../../../redux/notifications/actions';
import { keyByProperty } from '../../../../helpers/core';
import { getCurrentTimezone, getFormattedDate } from '../../../../helpers/dateTime';
import { t } from '../../../../i18n';

const api = new Api('promo_codes/:id');
const promocodesListApi = new Api('promo_codes');

function formatDateToUTC(date, time) {
  const convertedDate = moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD');
  const convertedTime = moment(time, ['h:mma']).format('HH:mm');
  const dateISOString = moment.tz(`${convertedDate} ${convertedTime}`, getCurrentTimezone()).toISOString();

  return dateISOString;
}

function* loadData(data) {
  try {
    const { id, isEdit } = data.payload;

    const result = yield call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      const { data: resultData } = result.data.success;
      const { starts_at, expire_at } = resultData;

      yield put(actions.loadDataSuccess({ data: resultData }));

      if (isEdit) {
        yield put(
          actions.edit({
            id,
            promoCode: {
              ...resultData,
              starts_at: starts_at ? moment.tz(starts_at, getCurrentTimezone()).format('YYYY-MM-DD') : null,
              expire_at: expire_at ? moment.tz(expire_at, getCurrentTimezone()).format('YYYY-MM-DD') : null,
              starts_time: starts_at
                ? getFormattedDate({ date: starts_at, formatKey: 'time_hours_minutes_long' })
                : null,
              expire_time: expire_at
                ? getFormattedDate({ date: expire_at, formatKey: 'time_hours_minutes_long' })
                : null,
            },
          }),
        );
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updatePromoCode(data) {
  try {
    const { payload } = data;
    const { editForm, id } = payload;
    const propsArray = Object.values(editForm);
    const promoCode = keyByProperty(propsArray, 'name', prop => prop.value);
    const { starts_at, expire_at, starts_time, expire_time } = promoCode;

    promoCode.starts_at = starts_at ? formatDateToUTC(starts_at, starts_time) : null;
    promoCode.expire_at = expire_at ? formatDateToUTC(expire_at, expire_time) : null;

    delete promoCode.starts_time;
    delete promoCode.expire_time;
    delete promoCode.status;
    delete promoCode.uses;
    delete promoCode.total;
    delete promoCode.country;

    const result = yield call(
      sendRequest,
      api.updateItem,
      { data: { promo_code: { ...promoCode } }, id },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(
        actions.saveSuccess({
          data: promoCode,
        }),
      );
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', {
            item: t('dash.admin.promoCodes.promo_code_title'),
          }),
        }),
      );

      yield put(push(`/promoCodes/${id}`));
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.check_validation_errors'),
        }),
      );

      yield put(
        actions.saveErrors({
          data: result,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* addPromoCode(data) {
  try {
    const { payload } = data;
    const { editForm } = payload;
    const propsArray = Object.values(editForm);
    const promoCode = keyByProperty(propsArray, 'name', prop => prop.value);
    const { starts_at, expire_at, starts_time, expire_time } = promoCode;

    promoCode.starts_at = starts_at ? formatDateToUTC(starts_at, starts_time) : null;
    promoCode.expire_at = expire_at ? formatDateToUTC(expire_at, expire_time) : null;

    delete promoCode.starts_time;
    delete promoCode.expire_time;
    delete promoCode.status;
    delete promoCode.uses;
    delete promoCode.total;
    delete promoCode.country;

    const result = yield call(
      sendRequest,
      promocodesListApi.createItem,
      { data: { promo_code: { ...promoCode } } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      const { id } = result.data;

      yield put(
        actions.saveSuccess({
          data: { ...promoCode, id },
        }),
      );

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.created_successfully', {
            item: t('dash.admin.promoCodes.promo_code_title'),
          }),
        }),
      );

      yield put(push(`/promoCodes/${id}`));
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.check_validation_errors'),
        }),
      );

      yield put(
        actions.saveErrors({
          data: result,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deletePromoCode(data) {
  try {
    const { payload } = data;
    const { id } = payload;

    yield call(sendRequest, api.deleteItem, { id }, notifications.REQUEST_ERROR);
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData() {
  try {
    const result = yield call(sendRequest, promocodesListApi.loadFormData, {}, notifications.REQUEST_ERROR);

    if (result && result.data && result.data.success && result.data.success.data) {
      const { data } = result.data.success;

      yield put(actions.loadFormData({ data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.SAVE, updatePromoCode),
    takeEvery(actions.DELETE, deletePromoCode),
    takeEvery(actions.ADD, addPromoCode),
    takeEvery(actions.EDIT, loadFormData),
    takeEvery(actions.CREATE, loadFormData),
  ]);
}
